"use client";

import { useEffect, useState } from "react";
import type {
  ITradingCompetitionTrader,
  ITradingCompetitionYou,
} from "@/sanity/pages/PageTradingCompetitionTemplate";

export interface LiveTrader {
  id: string;
  name: string;
  points: number;
  lastDay: number;
  /** The CMS value; scores drift around it and never run away from it. */
  base: number;
}

export interface LiveYou {
  rank: number;
  points: number;
  lastDay: number;
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const byPoints = (a: LiveTrader, b: LiveTrader) => b.points - a.points;

/**
 * A fake live feed for the blurred pre-launch board: every 1.1–1.9s one or two traders score (or
 * lose) points, the table re-sorts, and "your" row moves a little. Values are pulled back toward the
 * CMS numbers, so the board keeps looking like the design however long the page stays open.
 */
export function useLiveBoard(
  traders: ITradingCompetitionTrader[] | undefined,
  you: ITradingCompetitionYou | undefined,
  running: boolean,
) {
  const [rows, setRows] = useState<LiveTrader[]>(() =>
    (traders ?? [])
      .map((t, i) => ({
        id: t._key ?? `${i}`,
        name: t.name ?? "",
        points: t.points ?? 0,
        lastDay: t.lastDay ?? 0,
        base: t.points ?? 0,
      }))
      .sort(byPoints),
  );
  const [me, setMe] = useState<LiveYou>(() => ({
    rank: you?.rank ?? 0,
    points: you?.points ?? 0,
    lastDay: you?.lastDay ?? 0,
  }));

  useEffect(() => {
    if (!running || rows.length === 0) return;
    let timer: number;
    const baseRank = you?.rank ?? 0;
    const baseYou = you?.points ?? 0;

    const tick = () => {
      setRows((prev) => {
        const next = prev.map((t) => ({ ...t }));
        const hits = Math.random() < 0.35 ? 2 : 1;
        for (let h = 0; h < hits; h++) {
          // The podium moves less often and only upward, so its order holds.
          const podium = Math.random() < 0.3;
          const i = podium
            ? Math.floor(Math.random() * Math.min(3, next.length))
            : Math.min(next.length - 1, 3 + Math.floor(Math.random() * Math.max(1, next.length - 3)));
          const t = next[i];
          const pull = (t.base - t.points) * 0.25;
          const delta = Math.round((podium ? rand(20, 160) : rand(-280, 460)) + pull);
          t.points = Math.max(0, t.points + delta);
          t.lastDay += delta;
        }
        return next.sort(byPoints);
      });

      if (Math.random() < 0.55) {
        setMe((prev) => {
          const pull = (baseYou - prev.points) * 0.3;
          const delta = Math.round(rand(-60, 140) + pull);
          const step = delta > 70 ? -1 : delta < -30 ? 1 : 0;
          const rank = Math.min(baseRank + 3, Math.max(Math.max(1, baseRank - 3), prev.rank + step));
          return { rank, points: Math.max(0, prev.points + delta), lastDay: prev.lastDay + delta };
        });
      }

      timer = window.setTimeout(tick, rand(1100, 1900));
    };

    timer = window.setTimeout(tick, rand(600, 1200));
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, rows.length]);

  return { rows, me };
}

export const formatPoints = (n: number) => n.toLocaleString("en-US");

/** "+1,170" / "−373" (a real minus sign, as in the design). */
export const formatDelta = (n: number) => `${n < 0 ? "−" : "+"}${Math.abs(n).toLocaleString("en-US")}`;

/** "1st", "2nd", "3rd", then "04", "05"… as in the design. */
export const rankLabel = (rank: number) =>
  rank === 1 ? "1st" : rank === 2 ? "2nd" : rank === 3 ? "3rd" : String(rank).padStart(2, "0");
